import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

interface EmailLogRow {
  id: string;
  to_email: string;
  subject: string;
  template_key: string;
  status: 'queued' | 'sent' | 'failed' | 'bounced';
  error: string | null;
  sent_at: string | null;
  created_at: string;
}

/**
 * Diagnostic view for transactional email. Every send attempt — successful
 * or not — writes a row here via lib/mailjet.ts. Use this when emails aren't
 * arriving: a row with status='failed' tells you why (usually
 * mailjet_not_configured or sender_not_verified).
 */
export function AdminEmailLog() {
  const [rows, setRows] = useState<EmailLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<EmailLogRow[]>('/api/admin/email-log')
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const counts = rows.reduce(
    (a, r) => {
      a[r.status] = (a[r.status] ?? 0) + 1;
      return a;
    },
    {} as Record<string, number>
  );

  const allFailed =
    rows.length > 0 && rows.every((r) => r.status === 'failed');
  const mailjetNotConfigured =
    rows.some((r) => r.error === 'mailjet_not_configured');

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Email log</h1>
          <div className="sub">
            Latest 100 send attempts. If something didn&apos;t arrive, look here first.
          </div>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      {(allFailed || mailjetNotConfigured) && (
        <div className="a-alert a-alert-info">
          <strong>Mailjet isn&apos;t sending.</strong> The most common reasons:
          <ol style={{ margin: '8px 0 0 18px', padding: 0 }}>
            <li>
              <code>MAILJET_API_KEY</code>, <code>MAILJET_API_SECRET</code>, and{' '}
              <code>MAILJET_FROM_EMAIL</code> aren&apos;t set in Railway. Add
              them in your service&apos;s Variables tab and redeploy.
            </li>
            <li>
              The <code>MAILJET_FROM_EMAIL</code> address isn&apos;t verified in
              Mailjet. Go to Mailjet → Senders &amp; domains and confirm the
              email or domain.
            </li>
            <li>
              DKIM/SPF DNS records aren&apos;t set on your sending domain →
              messages are silently dropped or land in spam. Mailjet shows the
              records under Account Settings → Sender domains.
            </li>
          </ol>
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi">
          <div className="lbl">Total</div>
          <div className="val">{rows.length}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Sent</div>
          <div className="val" style={{ color: 'var(--a-good)' }}>
            {counts.sent ?? 0}
          </div>
        </div>
        <div className="kpi">
          <div className="lbl">Failed</div>
          <div className="val" style={{ color: 'var(--a-danger)' }}>
            {counts.failed ?? 0}
          </div>
        </div>
        <div className="kpi">
          <div className="lbl">Queued</div>
          <div className="val">{counts.queued ?? 0}</div>
        </div>
      </div>

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="a-empty">
            <div className="h">No emails attempted yet</div>
            <div className="p">
              Triggers: signup (welcome), checkout (order confirmation), admin
              actions (shipped, credit adjustment, fit-return outcomes).
            </div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>When</th>
                  <th>To</th>
                  <th>Template</th>
                  <th>Subject</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12 }}>
                      {new Date(r.created_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="id">{r.to_email}</td>
                    <td>
                      <span className="badge badge-neutral">{r.template_key}</span>
                    </td>
                    <td style={{ fontSize: 12 }}>{r.subject}</td>
                    <td>
                      <span
                        className={`badge ${
                          r.status === 'sent'
                            ? 'badge-good'
                            : r.status === 'failed'
                              ? 'badge-danger'
                              : r.status === 'queued'
                                ? 'badge-warn'
                                : 'badge-neutral'
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--a-muted)' }}>
                      {r.error ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
